import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  localStorageSafe,
  sessionStorageSafe,
  setLocalStorageItem
} from "../utils/storage/storage";
import {
  consoleLogError,
  consoleLogGroup,
  consoleLogInfo,
  consoleLogSuccess,
  consoleLogWarning
} from "../utils/console";
import { TIMEOUT_HOUR } from "../constants";
import { getVersionRequest } from "../api/BaseHostAPI";
import { msToTimeLabel } from "../utils/time";
import { isDevelopment, isProduction } from "../utils/assembly";
import {
  clearLocalStorage,
  clearSessionStorage,
  deleteAllCookies,
  getTokens,
  setTokens
} from "../utils/systemUtils/systemUtils";
import { unregisterServiceWorker } from "../utils/sw/sw";
import { dispatchCustomEvent } from "../utils/dom";
import {
  CUSTOM_EVENT_APP_VERSION_DIFFERS,
  STORAGE_KEY_REFRESH_COUNT
} from "../utils/storage/storage.constants";
import { LS_KEY_LAST_CHECK, LS_KEY_VERSION } from "./useCheckVersion.constants";
import { _sharedActionsSystem } from "@/shared/store/system/actions";

const scope = "Version";
const MS_FREQUENCY = TIMEOUT_HOUR;

const IS_CONSOLE_LOGS = isProduction() || false;

const checkVersion = (versionServer, onAppVersionDiffers) => {
  const versionClient = localStorageSafe.getItem(LS_KEY_VERSION);
  try {
    if (versionClient && versionServer && versionClient !== versionServer) {
      clearLocalStorage(true);
      clearSessionStorage(true);

      const { refreshToken } = getTokens();
      // EGWW-1226 save login and donateNotify on change versionServer
      deleteAllCookies();
      unregisterServiceWorker();
      setTokens(undefined, refreshToken);
      dispatchCustomEvent(CUSTOM_EVENT_APP_VERSION_DIFFERS);
      setLocalStorageItem(LS_KEY_VERSION, versionServer);

      let refreshCounts = parseInt(sessionStorageSafe.getItem(STORAGE_KEY_REFRESH_COUNT));
      if (isNaN(refreshCounts)) {
        refreshCounts = 0;
      }

      if (refreshCounts) {
        consoleLogError("Version", "Error! App reloaded once, but versions differ anyway.", {
          versionClient,
          versionServer,
          refreshCounts,
        });
      } else {
        sessionStorageSafe.setItem(STORAGE_KEY_REFRESH_COUNT, refreshCounts + 1);
        consoleLogWarning("Version", "Differs! App will be reloaded in 3s!");
        consoleLogGroup("More info about content versions", {
          server: versionServer, client: versionClient,
        });

        onAppVersionDiffers();

        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } else {
      setLocalStorageItem(LS_KEY_VERSION, versionServer || versionClient);
      sessionStorageSafe.setItem(STORAGE_KEY_REFRESH_COUNT, 0);

      if (IS_CONSOLE_LOGS) {
        consoleLogSuccess("Version", "Version is OK!");
        consoleLogGroup("More info about versions", {
          client: versionClient,
          server: versionServer,
        });
      }
    }
  } catch (e) {
    console.log(e);
  }
};

const useCheckVersion = (isEnabled = true) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    if (isDevelopment()) {
      return undefined;
    }

    let timerId;

    const checkAfter = (ms) => {
      if (IS_CONSOLE_LOGS) {
        consoleLogInfo(scope, `Check in ${msToTimeLabel(ms)}`);
      }

      timerId = setTimeout(async () => {
        if (IS_CONSOLE_LOGS) {
          consoleLogInfo(scope, "Checking...");
        }

        setLocalStorageItem(LS_KEY_LAST_CHECK, Date.now());

        await getVersionRequest().then((serverVersion) => {
          if (serverVersion) {
            checkVersion(serverVersion, () => {
              dispatch(_sharedActionsSystem.toast(
                "This webpage needs updating,</br>" +
                "and will reload in 3 seconds."
              ))
            });
          }
        });

        checkAfter(MS_FREQUENCY);
      }, ms);
    };

    checkAfter(0);

    return () => {
      clearTimeout(timerId);
    };
  }, [isEnabled]);
};

export default useCheckVersion;
